/**
 * Map PlotSpec → function-plot options (math engine).
 * @see https://mauriciopoppe.github.io/function-plot/
 */

import type {
  FunctionPlotAnnotation,
  FunctionPlotDatum,
  FunctionPlotOptions,
} from 'function-plot';
import type { Mark, PlotSpec, Series } from '../plots/plot-spec.js';

const PALETTE = [
  '#0f766e',
  '#c2410c',
  '#1d4ed8',
  '#7c3aed',
  '#be123c',
  '#a16207',
  '#0e7490',
  '#4d7c0f',
] as const;

function colorFor(s: Series, index: number): string {
  return s.color ?? PALETTE[index % PALETTE.length]!;
}

/**
 * Map our plot-spec DSL → function-plot / interval-arithmetic symbols.
 * - ln → log (library has log, not ln)
 * - pi → PI, e → E (library constants)
 */
export function toFunctionPlotExpr(expr: string): string {
  return expr
    .replace(/\bln\b/g, 'log')
    .replace(/\bpi\b/g, 'PI')
    .replace(/\be\b/g, 'E');
}

function seriesToDatum(s: Series, index: number, scope: Record<string, number>): FunctionPlotDatum {
  const color = colorFor(s, index);
  const attr =
    s.strokeWidth !== undefined
      ? { 'stroke-width': String(s.strokeWidth) }
      : undefined;

  if (s.type === 'explicit') {
    const d: FunctionPlotDatum = {
      fn: toFunctionPlotExpr(s.expr),
      fnType: 'linear',
      graphType: 'polyline',
      sampler: 'builtIn',
      color,
      scope: { ...scope },
    };
    if (attr) d.attr = attr;
    return d;
  }
  if (s.type === 'parametric') {
    const d: FunctionPlotDatum = {
      x: toFunctionPlotExpr(s.xExpr),
      y: toFunctionPlotExpr(s.yExpr),
      fnType: 'parametric',
      graphType: 'polyline',
      sampler: 'builtIn',
      range: [s.tMin, s.tMax],
      color,
      scope: { ...scope },
    };
    if (attr) d.attr = attr;
    return d;
  }
  if (s.type === 'implicit') {
    const d: FunctionPlotDatum = {
      fn: toFunctionPlotExpr(s.expr),
      fnType: 'implicit',
      color,
      scope: { ...scope },
    };
    if (attr) d.attr = attr;
    return d;
  }
  throw new Error(`function-plot does not support series type ${s.type}`);
}

function applyMarks(
  data: FunctionPlotDatum[],
  series: Series[],
  marks: Mark[] | undefined,
): FunctionPlotAnnotation[] {
  if (!marks || marks.length === 0) return [];
  const annotations: FunctionPlotAnnotation[] = [];
  const pointCoords: number[][] = [];

  for (const m of marks) {
    if (m.type === 'vline') {
      const a: FunctionPlotAnnotation = { x: m.x };
      if (m.label) a.text = m.label;
      annotations.push(a);
    } else if (m.type === 'hline') {
      const a: FunctionPlotAnnotation = { y: m.y };
      if (m.label) a.text = m.label;
      annotations.push(a);
    } else if (m.type === 'point') {
      pointCoords.push([m.x, m.y]);
    } else if (m.type === 'tangent') {
      const idx = series.findIndex((s) => s.id === m.seriesId);
      if (idx < 0) continue;
      const s = series[idx]!;
      if (s.type !== 'explicit') continue;
      const d = data[idx]!;
      // Static secant as visual tangent approximation (function-plot API)
      d.secants = d.secants ?? [];
      d.secants.push({ x0: m.at, x1: m.at + 0.35 });
    }
  }

  if (pointCoords.length > 0) {
    data.push({
      points: pointCoords,
      fnType: 'points',
      graphType: 'scatter',
      color: '#57534e',
    });
  }
  return annotations;
}

export function buildFunctionPlotOptions(
  spec: PlotSpec,
  target: HTMLElement,
  size: { width: number; height: number },
): FunctionPlotOptions {
  const scope: Record<string, number> = {};
  if (spec.animation) {
    scope[spec.animation.param] = spec.animation.from;
  }

  const data: FunctionPlotDatum[] = [];
  for (let i = 0; i < spec.series.length; i++) {
    data.push(seriesToDatum(spec.series[i]!, i, scope));
  }

  const annotations = applyMarks(data, spec.series, spec.marks);

  let xDomain: [number, number] = [spec.viewport.xMin, spec.viewport.xMax];
  let yDomain: [number, number] = [spec.viewport.yMin, spec.viewport.yMax];
  if (spec.viewport.equalAspect) {
    const xr = xDomain[1] - xDomain[0];
    const yr = yDomain[1] - yDomain[0];
    const cx = (xDomain[0] + xDomain[1]) / 2;
    const cy = (yDomain[0] + yDomain[1]) / 2;
    const pixelAspect = size.width / Math.max(1, size.height);
    let halfX = xr / 2;
    let halfY = yr / 2;
    const domainAspect = halfX / halfY;
    if (domainAspect > pixelAspect) {
      halfY = halfX / pixelAspect;
    } else {
      halfX = halfY * pixelAspect;
    }
    xDomain = [cx - halfX, cx + halfX];
    yDomain = [cy - halfY, cy + halfY];
  }

  const opts: FunctionPlotOptions = {
    target,
    width: size.width,
    height: size.height,
    grid: true,
    xAxis: { domain: xDomain, label: 'x' },
    yAxis: { domain: yDomain, label: 'y' },
    data,
  };
  if (spec.title) opts.title = spec.title;
  if (annotations.length > 0) opts.annotations = annotations;
  return opts;
}
